import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { Theme, ThemeOptions } from '@mui/material'
import { createTheme } from '@mui/material'
import { blue } from '@mui/material/colors'
import { frFR as coreFr } from '@mui/material/locale'
import { GridLocaleText } from '@mui/x-data-grid'
import { frFR as dataGridFR } from '@mui/x-data-grid/locales'
import type {} from '@mui/x-data-grid/themeAugmentation'
import { frFR as datePickerFr } from '@mui/x-date-pickers/locales'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { forwardRef } from 'react'
import { Link, LinkProps as RouterLinkProps } from 'react-router-dom'

const LinkComponent = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  ({ href, ...other }, ref) => {
    // Map href (MUI) -> to (react-router)
    return <Link ref={ref} to={href} {...other} />
  },
)
LinkComponent.displayName = 'LinkComponent'

export const GLOBAL_OVERRIDES: ThemeOptions = {
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkComponent,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiListItemButton: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiIconButton: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiInputBase: {
      defaultProps: {
        // Needed to prevent adding a global style for every input field
        disableInjectingGlobalStyles: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'text',
        LinkComponent,
        loadingPosition: 'start',
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.smallPdLeft': {
            paddingLeft: theme.spacing(1),
            [theme.breakpoints.up('sm')]: {
              paddingLeft: theme.spacing(2),
            },
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
        }),
        columnHeaderCheckbox: ({ theme }) => ({
          '.MuiButtonBase-root': {
            color: theme.palette.secondary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.main,
            },
          },
        }),
        columnHeaderTitle: {
          fontWeight: 'bold',
        },
        sortIcon: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
        }),
        columnSeparator: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
          ':hover': {
            color: theme.palette.secondary.contrastText,
          },
        }),
        menuIcon: ({ theme }) => ({
          '& .MuiButtonBase-root': {
            color: theme.palette.secondary.contrastText,
            ':hover': {
              color: theme.palette.secondary.contrastText,
            },
          },
        }),
        cell: {
          '&.strong': {
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          color: theme.palette.secondary.contrastText,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '.MuiDataGrid-root:not(:first-of-type)': {
            borderRadius: '0 !important',
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    },
    MuiTab: {
      defaultProps: {
        LinkComponent,
      },
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        subtitle2: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontWeight: 'bold',
        }),
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <ExpandMoreIcon />,
      },
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        clearOnEscape: true,
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          minHeight: 4, // Ensures the progress bar has a minimum height so that it can't shrink
        },
      },
    },
  },
}

const customGridFRLocaleOverride: Partial<GridLocaleText> = {
  // La traduction est tout simplement manquante dans la locale frFR de MUI Grid
  paginationDisplayedRows: ({ from, to, count, estimated }) => {
    if (!estimated) {
      return `${from}–${to} de ${count !== -1 ? count : `plus de ${to}`}`
    }
    const estimatedLabel = estimated && estimated > to ? `d'environ ${estimated}` : `de plus de ${to}`
    return `${from}–${to} ${count !== -1 ? `de ${count}` : estimatedLabel}`
  },
}

dataGridFR.components.MuiDataGrid.defaultProps.localeText = {
  ...dataGridFR.components.MuiDataGrid.defaultProps.localeText,
  ...customGridFRLocaleOverride,
}

// Color picking documentation https://m2.material.io/design/color/the-color-system.html#tools-for-picking-colors
// Custom color: https://mui.com/material-ui/customization/palette/#custom-colors
// Generating token color: https://mui.com/material-ui/customization/color/#playground
export const createCustomTheme = (language?: string): Theme => {
  return createTheme(
    {
      ...GLOBAL_OVERRIDES,
      colorSchemes: {
        dark: {
          palette: {
            primary: {
              main: blue[200],
            },
            secondary: {
              main: '#14233a',
            },
          },
        },
        light: {
          palette: {
            primary: {
              main: blue[700],
            },
            secondary: {
              main: '#14233a',
            },
          },
        },
      },
    },
    ...(language === 'fr' ? [coreFr, dataGridFR, datePickerFr] : []),
  )
}
